import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Shield, Upload, Download, Calendar, AlertTriangle, CheckCircle, Clock } from '../ui/icons';
import { usePermissions } from '../../hooks/usePermissions';
import { certificatesApi } from '../../services/api';
import { Certificate } from '../../services/api';

const MyCertificates: React.FC = () => {
  const permissions = usePermissions();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      if (permissions.tenantId) {
        const response = await certificatesApi.getByTenant(permissions.tenantId);
        setCertificates(response.data.certificates);
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCertificateStatus = (cert: Certificate) => {
    const now = new Date();
    const validTo = new Date(cert.valid_to);
    const validFrom = new Date(cert.valid_from);
    const daysToExpiry = Math.ceil((validTo.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (now < validFrom) {
      return { status: 'not_active', label: 'No Activo', color: 'gray', icon: Clock };
    } else if (daysToExpiry <= 0) {
      return { status: 'expired', label: 'Vencido', color: 'red', icon: AlertTriangle };
    } else if (daysToExpiry <= 30) {
      return { status: 'expiring', label: 'Por Vencer', color: 'orange', icon: AlertTriangle };
    } else {
      return { status: 'active', label: 'Activo', color: 'green', icon: CheckCircle };
    }
  };

  const getStatusBadge = (cert: Certificate) => {
    const { status, label, color } = getCertificateStatus(cert);

    switch (color) {
      case 'green':
        return <Badge variant="default" className="bg-green-100 text-green-800">✅ {label}</Badge>;
      case 'orange':
        return <Badge variant="destructive" className="bg-orange-100 text-orange-800">⚠️ {label}</Badge>;
      case 'red':
        return <Badge variant="destructive">❌ {label}</Badge>;
      case 'gray':
        return <Badge variant="secondary">⏳ {label}</Badge>;
      default:
        return <Badge variant="secondary">{label}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleUploadCertificate = () => {
    // Create hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.p12,.pfx';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && permissions.tenantId) {
        const password = prompt('Ingrese la contraseña del certificado:');
        if (password) {
          try {
            setUploadLoading(true);
            await certificatesApi.upload(permissions.tenantId, file, password);
            loadCertificates(); // Reload certificates
          } catch (error) {
            console.error('Error uploading certificate:', error);
            alert('Error al subir el certificado. Verifique la contraseña.');
          } finally {
            setUploadLoading(false);
          }
        }
      }
    };
    input.click();
  };

  const handleDownloadCertificate = async (certId: string, filename: string) => {
    try {
      const response = await certificatesApi.download(certId);
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando mis certificados...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Mis Certificados</h1>
          <p className="text-muted-foreground">Gestiona tus certificados digitales para firma electrónica</p>
        </div>
        <Button
          onClick={handleUploadCertificate}
          disabled={uploadLoading}
          className="flex items-center space-x-2"
        >
          <Upload className="h-4 w-4" />
          <span>{uploadLoading ? 'Subiendo...' : 'Subir Certificado'}</span>
        </Button>
      </div>

      {/* Certificates Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">
                  {certificates.filter(cert => getCertificateStatus(cert).status === 'active').length}
                </p>
                <p className="text-xs text-muted-foreground">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">
                  {certificates.filter(cert => getCertificateStatus(cert).status === 'expiring').length}
                </p>
                <p className="text-xs text-muted-foreground">Por Vencer</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold">
                  {certificates.filter(cert => getCertificateStatus(cert).status === 'expired').length}
                </p>
                <p className="text-xs text-muted-foreground">Vencidos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{certificates.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificates List */}
      <Card>
        <CardHeader>
          <CardTitle>Certificados Digitales</CardTitle>
        </CardHeader>
        <CardContent>
          {certificates.length > 0 ? (
            <div className="space-y-4">
              {certificates.map((cert) => {
                const certStatus = getCertificateStatus(cert);
                const daysToExpiry = Math.ceil((new Date(cert.valid_to).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

                return (
                  <div key={cert.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <Shield className="h-6 w-6 text-blue-600" />
                          <div>
                            <h3 className="font-medium">{cert.filename}</h3>
                            <p className="text-sm text-muted-foreground">
                              Número de Serie: {cert.serial_number}
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm">
                              <strong>Emisor:</strong> {cert.issuer}
                            </p>
                            <p className="text-sm">
                              <strong>Sujeto:</strong> {cert.subject}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm">
                              <strong>Válido desde:</strong> {formatDate(cert.valid_from)}
                            </p>
                            <p className="text-sm">
                              <strong>Válido hasta:</strong> {formatDate(cert.valid_to)}
                            </p>
                            {certStatus.status === 'active' && daysToExpiry <= 90 && (
                              <p className="text-sm text-orange-600">
                                ⚠️ Vence en {daysToExpiry} días
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 ml-4">
                        {getStatusBadge(cert)}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadCertificate(cert.id, cert.filename)}
                          title="Descargar certificado"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Shield className="mx-auto h-16 w-16 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No tienes certificados</h3>
              <p className="mt-2 text-muted-foreground">
                Sube tu primer certificado digital para comenzar a firmar documentos electrónicos
              </p>
              <Button
                onClick={handleUploadCertificate}
                disabled={uploadLoading}
                className="mt-4"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadLoading ? 'Subiendo...' : 'Subir Primer Certificado'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Certificate Information */}
      <Card>
        <CardHeader>
          <CardTitle>Información sobre Certificados</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900">💡 ¿Qué es un certificado digital?</h4>
              <p className="text-sm text-blue-800 mt-1">
                Un certificado digital es un archivo que contiene tu identidad digital y te permite
                firmar documentos electrónicos de manera segura, garantizando su autenticidad e integridad.
              </p>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg">
              <h4 className="font-medium text-orange-900">⚠️ Certificados próximos a vencer</h4>
              <p className="text-sm text-orange-800 mt-1">
                Te notificaremos 30 días antes del vencimiento de tus certificados.
                Asegúrate de renovarlos a tiempo para evitar interrupciones en tus operaciones.
              </p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900">🔒 Seguridad</h4>
              <p className="text-sm text-green-800 mt-1">
                Tus certificados están almacenados de forma segura y encriptada.
                Solo tú puedes acceder a ellos con las credenciales adecuadas.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyCertificates;